import React, { useState, useEffect, useRef } from 'react';
import BreadCrumbs from '../../../../components/common/BreadCrumbs/BreadCrumbs';
import ScrollToTopBtn from '../../../../components/common/ScrollToTopBtn';
import Loader from '../../../../components/common/Loader';
import Notifications from '../../../../components/common/Notifications';
import Modal from '../../../../components/common/Modal';
import { useNavigate } from 'react-router-dom';
import ErrorIcon from '../../../../../public/assets/images/errorCircleSmall.svg';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { fetchCannedResponses, fetchCannedResponseById, updateCannedResponse, createCannedResponse, deleteCannedResponse, selectCannedResponseOptions } from '../../../../store/slices/Tuition/manageCannedResponseSlice';
import { fetchProfile } from '../../../../store/slices/profileSlice';
import { trackButtonClick, trackNavigation } from '../../../../services/analytics';
import { decodeHtmlEntities } from '../../../../utils/htmlUtils';

interface ResponseForm {
  id: string;
  title: string;
  subject: string;
  message: string;
}

const ManageCannedResponses: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cannedResponseOptions = useAppSelector(selectCannedResponseOptions);
  const { loading, cannedResponses, error } = useAppSelector((state) => state.tuitionManageCannedResponse);

  const { adminProfile } = useAppSelector((state) => state.profile);
  
  const profileLoading = useAppSelector((state) => state.profile.loading);
  
  const [responseForms, setResponseForms] = useState<ResponseForm[]>([
    { id: '1', title: '', subject: '', message: '' }
  ]);
  const [showForms, setShowForms] = useState(false);
  const [isEditingCannedResponse, setIsEditingCannedResponse] = useState(false);
  const [currentCannedResponseId, setCurrentCannedResponseId] = useState<number | null>(null);
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [isErrorNotification, setIsErrorNotification] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deletedResponseName, setDeletedResponseName] = useState<string>('');
  const [showDeleteBanner, setShowDeleteBanner] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<{title: string, subject: string, message: string}>({title: '', subject: '', message: ''});
  const MAX_WORDS = 1000;

  const initialSubjectRef = useRef('');
  const initialMessageRef = useRef('');

  useEffect(() => {
    dispatch(fetchCannedResponses());
  }, [dispatch]);

  // Handle error notification from fetchCannedResponses
  useEffect(() => {
    if (error) {
      setNotificationMessage(error);
      setIsErrorNotification(true);
      setShowNotification(true);
      // Hide notification after 5 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    }
  }, [error]);

  // Effect to populate form when a canned response is selected
  useEffect(() => {
    // Only run this effect if the form is actually showing AND we're editing a canned response
    // AND we have a valid canned response ID selected
    if (!showForms || !isEditingCannedResponse || !currentCannedResponseId) return;
    
    if (cannedResponses.length > 0 && responseForms.length > 0) {
      const currentForm = responseForms[0];
      if (currentForm.title) {
        const selectedResponse = cannedResponses.find(
          (response) => response.crName === currentForm.title
        );
        
        if (selectedResponse) {
          const subject = decodeHtmlEntities(selectedResponse.crSubject);
          const message = decodeHtmlEntities(selectedResponse.crBody);
          setResponseForms([{
            id: currentForm.id,
            title: selectedResponse.crName,
            subject,
            message
          }]);
          // Store initial values for revert logic
          initialSubjectRef.current = subject;
          initialMessageRef.current = message;
        }
      }
    }
  }, [cannedResponses, showForms, isEditingCannedResponse, currentCannedResponseId]);
   
  useEffect(() => {
    if (!adminProfile && !profileLoading) {
      dispatch(fetchProfile());
    }
  }, [adminProfile, profileLoading, dispatch]);

  const handlePreviousClick = () => {
    console.log('📊 Analytics: Navigate back to admin');
    trackNavigation('manage-canned-responses', 'admin', 'back');
    navigate('/admin');
  };

  const handleAddResponse = () => {
    console.log('📊 Analytics: Add new canned response clicked');
    trackButtonClick('Add Canned Response', 'manage-canned-responses');
    const newForm: ResponseForm = {
      id: Date.now().toString(),
      title: '',
      subject: '',
      message: ''
    };
    setResponseForms([newForm]);
    setShowForms(true);
    setIsEditingCannedResponse(false);
    setCurrentCannedResponseId(null);
    initialSubjectRef.current = '';   //HRCOM-103969
    initialMessageRef.current = '';   //HRCOM-103969
  };

  const handleTitleChange = (id: string, value: string) => {
    setResponseForms(responseForms.map(form =>
      form.id === id ? { ...form, title: value } : form
    ));
    // Clear title error when user starts typing
    if (formErrors.title && value.trim()) {
      setFormErrors(prev => ({...prev, title: ''}));
    }
  };

  const handleSubjectChange = (id: string, value: string) => {
    setResponseForms(responseForms.map(form =>
      form.id === id ? { ...form, subject: value } : form
    ));
    // Clear subject error when user starts typing
    if (formErrors.subject && value.trim()) {
      setFormErrors(prev => ({...prev, subject: ''}));
    }
  };

  const handleMessageChange = (id: string, value: string) => {
    // Count words in the message
    const words = value.trim().split(/\s+/).filter(word => word.length > 0);
    const wordCount = words.length;
    
    // Get current form to check if user is deleting text
    const currentForm = responseForms.find(f => f.id === id);
    const isDeleting = currentForm ? value.length < currentForm.message.length : false;
    
    // Only update if word count is within limit or if user is deleting text
    if (wordCount <= MAX_WORDS || isDeleting) {
      setResponseForms(responseForms.map(form =>
        form.id === id ? { ...form, message: value } : form
      ));
      // Clear message error when user starts typing
      if (formErrors.message && value.trim()) {
        setFormErrors(prev => ({...prev, message: ''}));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const form = responseForms[0];
    
    // Validate form fields
    const errors = {title: '', subject: '', message: ''};
    let hasErrors = false;
    
    if (!form.title.trim()) {
      errors.title = 'Name is required';
      hasErrors = true;
    }
    
    if (!form.subject.trim()) {
      errors.subject = 'Subject is required';
      hasErrors = true;
    }
    
    if (!form.message.trim()) {
      errors.message = 'Message is required';
      hasErrors = true;
    }
    
    setFormErrors(errors);
    
    // If there are errors, don't proceed with submission
    if (hasErrors) {
      return;
    }
    
    // If editing a canned response, update it via API
    if (isEditingCannedResponse && currentCannedResponseId) {
      try {
        // When editing, only send subject and body (not the name/title)
        const encodedData = {
          crSubject: encodeURIComponent(form.subject),
          crBody: encodeURIComponent(form.message.replaceAll('\n', '<br>'))
        };
        
        await dispatch(updateCannedResponse({
          id: currentCannedResponseId,
          data: encodedData
        })).unwrap();
        
        console.log('Canned response updated successfully');
        
        // Show success notification
        setNotificationMessage(`${form.title} has been updated.`);
        setIsErrorNotification(false);
        setShowNotification(true);
        
        // Hide notification after 5 seconds
        setTimeout(() => {
          setShowNotification(false);
        }, 5000);
        
        // IMPORTANT: Clear editing state BEFORE refreshing the list
        // This prevents the useEffect from repopulating the form
        setIsEditingCannedResponse(false);
        setCurrentCannedResponseId(null);
        
        // Reset to empty form
        setResponseForms([{ id: '1', title: '', subject: '', message: '' }]);
        
        // Refresh the canned responses list to update the UI
        await dispatch(fetchCannedResponses()).unwrap();
        
        // Close the form and show only the list
        setShowForms(false);
        
      } catch (error) {
        console.error('Error updating canned response:', error);
      }
    } else {
      // Creating a new canned response - include the name/title
      try {
        const encodedData = {
          crName: form.title,
          crSubject: encodeURIComponent(form.subject),
          crBody: encodeURIComponent(form.message.replaceAll('\n', '<br>'))
        };
        
        await dispatch(createCannedResponse(encodedData)).unwrap();
        
        // Show success notification
        setNotificationMessage(`${form.title} has been created.`);
        setIsErrorNotification(false);
        setShowNotification(true);
        
        // Hide notification after 5 seconds
        setTimeout(() => {
          setShowNotification(false);
        }, 5000);
        
        // Refresh the canned responses list to update the UI with the new response
        await dispatch(fetchCannedResponses()).unwrap();
        
        // Reset to empty form
        setResponseForms([{ id: '1', title: '', subject: '', message: '' }]);
        
        // Close the form and show only the list
        setShowForms(false);
        
      } catch (error) {
        console.error('Error creating canned response:', error);
      }
    }
  };

  const handleClear = () => {
    setResponseForms([{
      id: responseForms[0]?.id || '1',
      // When editing, keep the current title (name) since it's disabled; when adding, clear the title
      title: isEditingCannedResponse ? (responseForms[0]?.title || '') : '',
      subject: initialSubjectRef.current,
      message: initialMessageRef.current
    }]);
    setFormErrors({title: '', subject: '', message: ''});
  };

  const handleDelete = () => {
    if (!currentCannedResponseId) return;
    
    // Show the delete confirmation modal
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!currentCannedResponseId) return;
    
    // Save the current response name before deleting
    const currentResponseName = responseForms[0]?.title || '';
    
    try {
      await dispatch(deleteCannedResponse(currentCannedResponseId)).unwrap();
      
      console.log('Canned response deleted successfully');
      
      // Close the modal
      setShowDeleteModal(false);
      
      // Show delete success banner
      setDeletedResponseName(currentResponseName);
      setShowDeleteBanner(true);
      
      // Hide banner after 5 seconds
      setTimeout(() => {
        setShowDeleteBanner(false);
      }, 5000);
      
      // Clear editing state
      setIsEditingCannedResponse(false);
      setCurrentCannedResponseId(null);
      
      // Reset to empty form
      setResponseForms([{ id: '1', title: '', subject: '', message: '' }]);
      
      // Refresh the canned responses list to update the UI
      await dispatch(fetchCannedResponses()).unwrap();
      
      // Close the form and show only the list with success banner
      setShowForms(false);
      
    } catch (error) {
      console.error('Error deleting canned response:', error);
      setShowDeleteModal(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleCannedResponseClick = async (crId: string) => {
    const crIdNumber = Number(crId);
    
    // Clear form errors when clicking on a canned response
    setFormErrors({title: '', subject: '', message: ''});
    
    // Store the ID for later use when saving
    setCurrentCannedResponseId(crIdNumber);
    
    // First, show the form immediately with basic data from cannedResponseOptions
    const basicResponse = cannedResponseOptions.find(opt => opt.crId === crIdNumber);
    
    if (basicResponse) {
      // Show form with category/title first
      setResponseForms([{
        id: responseForms[0]?.id || '1',
        title: basicResponse.crName,
        subject: '',
        message: ''
      }]);
      setShowForms(true);
      setIsEditingCannedResponse(true);
      // Reset initial values
      initialSubjectRef.current = '';
      initialMessageRef.current = '';
    }
    
    // Then fetch the full details and update
    try {
      const result = await dispatch(fetchCannedResponseById(crIdNumber)).unwrap();
      
      // Update the form with full details
      setResponseForms([{
        id: responseForms[0]?.id || '1',
        title: result.crName,
        subject: decodeHtmlEntities(result.crSubject),
        message: decodeHtmlEntities(result.crBody)
      }]);
      // Store initial values for revert logic
      initialSubjectRef.current = decodeHtmlEntities(result.crSubject);
      initialMessageRef.current = decodeHtmlEntities(result.crBody);
    } catch (error) {
      console.error('Error fetching canned response:', error);
    }
  };

  // Show loader while profile is loading
  if (profileLoading) {
    return <Loader />;
  }

  // Check if user has ViewOnlyUser role and deny access
  const isViewOnlyUser = adminProfile?.groupName === 'epf.WorkforceRetrieverViewOnlyUser';
  console.log('ManageCannedResponses - isViewOnlyUser:', isViewOnlyUser);
  console.log('profile groupName:', adminProfile);
   return (
     <div className="lg:mx-40! mx-5! min-h-[70vh]">
      {loading && <Loader />}
     
      {isViewOnlyUser ? (
        <div className="w-full flex items-center justify-center min-h-[300px]">
          <div className="w-full max-w-md">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4">
                <img src={ErrorIcon} alt="access denied" className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="h3 text-red-600 mb-2">Access denied</h3>
              <p className="text-gray-700 text-sm">You do not have permission to manage canned responses.</p>
            </div>
          </div>
        </div>
      ) : (
      <>
         <BreadCrumbs
        previousPage="Workplace development"
        currentPage="Manage canned responses"
        onPreviousClick={handlePreviousClick}
      />
      <div style={{margin: 'auto 10%'}}  className="mx-5 md:mx-10 lg:mx-[20%]">
      <h1 className="h1 mt-8!">Manage canned responses</h1>

      {showDeleteBanner && (
        <div
          className="ds-banner ds-banner__bg--white ds-banner--icon !mb-[24px]"
          data-ds-variant="basic"
          data-ds-version="1"
          data-ds-theme="vessel"
          aria-labelledby="delete-banner-title"
          style={{ border: '1px solid var(--Color-Blue-Deep, #003B71)', padding: '1px' }}
        >
          <div className="ds-banner__icon-img">
            <img
              src="/assets/images/informationIcon.svg"
              alt=""
              style={{ width: '38px', height: '38px' }}
            />
          </div>
          <div className="ds-banner__container">
            <div className="ds-banner__main flex flex-col">
              <div className="ds-banner__main-body p1 -medium"  style={{ color: 'var(--Color-Inky, #0D1C3D)', marginTop: '7px' }}>
                {deletedResponseName} has been deleted.
              </div>
            </div>
          </div>
        </div>
      )}

      {showNotification && (
        <Notifications messageInfo={notificationMessage} status={!isErrorNotification} />
      )}

      {!showForms && (
        <div className="flex gap-2 mt-8" style={{marginBottom: '24px'}}>
          <button
            type="button"
            onClick={handleAddResponse}
            className="button -secondary"
            data-analytics-type="button"
            data-analytics-click="Add another response"
          >
            + Add another response
          </button>
        </div>
      )}
  
      {/* Two-column layout: Form on left, List on right */}
      <div className={showForms ? "flex gap-16 items-start" : ""}>
        {/* Left column - Form */}
        {showForms && (
          <div className="flex-1">
            <form onSubmit={handleSubmit} className="flex flex-col gap-[32px] flex-wrap w-full">
              {responseForms.map((form) => (
                <div key={form.id}>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="h3 mb-0">
                      {isEditingCannedResponse ? 'Edit a response' : 'Add a new canned response'}
                    </h2>
                  </div>
    
                  <div className="grid grid-cols-1 gap-x-4 gap-y-8 w-full">
                    <div className="ds-form w-full" data-ds-theme="vessel" data-ds-variant="input" data-ds-version="1">
                      <label className="after:content-['*'] after:text-[#D1480C] after:ml-0.5" htmlFor={`title-${form.id}`}>Name</label>
                      <div className="w-full relative">
                        <input
                          autoComplete="on"
                          className={`w-full min-w-0 block !text-[#0D1C3D] ${formErrors.title ? 'border-red-500' : ''}`}
                          style={{ width: '100%' }}
                          id={`title-${form.id}`}
                          name={`title-${form.id}`}
                          type="text"
                          value={form.title}
                          onChange={(e) => handleTitleChange(form.id, e.target.value)}
                          placeholder="Enter name"
                          disabled={isEditingCannedResponse}
                          maxLength={32}
                        />
                      </div>
                      {formErrors.title && (
                        <div className="mt-1 text-red-600 text-sm flex items-center gap-1">
                          <img src={ErrorIcon} alt="Error" className="w-4 h-4" />
                          {formErrors.title}
                        </div>
                      )}
                      <div className="mt-1">
                        <span className={`text-sm ${form.title.length > 29 ? 'text-red-600' : 'text-gray-600'}`}>
                          {32 - form.title.length} of 32 characters left
                        </span>
                      </div>
                    </div>
    
                    <div className="ds-form w-full" data-ds-theme="vessel" data-ds-variant="input" data-ds-version="1">
                      <label htmlFor={`subject-${form.id}`} className="after:content-['*'] after:text-[#D1480C] after:ml-0.5" >Subject</label>
                      <div className="w-full relative">
                        <input
                          autoComplete="on"
                          className={`w-full min-w-0 block !text-[#0D1C3D] ${formErrors.subject ? 'border-red-500' : ''}`}
                          style={{ width: '100%' }}
                          id={`subject-${form.id}`}
                          name={`subject-${form.id}`}
                          type="text"
                          value={form.subject}
                          onChange={(e) => handleSubjectChange(form.id, e.target.value)}
                          placeholder="Enter subject line"
                          maxLength={250}
                        />
                      </div>
                      {formErrors.subject && (
                        <div className="mt-1 text-red-600 text-sm flex items-center gap-1">
                          <img src={ErrorIcon} alt="Error" className="w-4 h-4" />
                          {formErrors.subject}
                        </div>
                      )}
                      <div className="mt-1">
                        <span className={`text-sm ${form.subject.length > 225 ? 'text-red-600' : 'text-gray-600'}`}>
                          {250 - form.subject.length} of 250 characters left
                        </span>
                      </div>
                    </div>
    
                    <div className="form-group w-full">
                      <div className="textarea-wrapper w-full relative">
                        <p id={`message-label-${form.id}`} className="p3 -medium after:content-['*'] after:text-[#D1480C] after:ml-0.5">Message</p>
                        <textarea
                          autoComplete="on"
                          id={`message-${form.id}`}
                          name={`message-${form.id}`}
                          aria-labelledby={`message-label-${form.id}`}
                          className={`text-area_word-count text-area_global -book !text-base !text-[#0D1C3D] !w-full ${formErrors.message ? 'border-red-500' : ''}`}
                          data-show-lines="5"
                          rows={6}
                          value={form.message}
                          onChange={(e) => handleMessageChange(form.id, e.target.value)}
                          placeholder="Enter the message text"
                          maxLength={4000}
                        />
                        {formErrors.message && (
                          <div className="mt-1 text-red-600 text-sm flex items-center gap-1">
                            <img src={ErrorIcon} alt="Error" className="w-4 h-4" />
                            {formErrors.message}
                          </div>
                        )}
                        <div className="flex justify-between items-center mt-2">
                          <span className={`text-sm ${form.message.length > 3600 ? 'text-red-600' : 'text-gray-600'}`}>
                            {4000 - form.message.length} of 4000 characters left
                          </span>
                          {isEditingCannedResponse && (
                            <button
                              type="button"
                              onClick={handleDelete}
                              className="text-[#D1480C] hover:underline text-sm p3 -medium flex items-center gap-1"
                              style={{ marginTop: '22px', color: 'var(--Link, #006BA6)' }}
                              data-analytics-type="button"
                              data-analytics-click="Delete response"
                            >
                              <img src="/assets/images/trash-solid.svg" alt="Delete" className="w-4 h-4" />
                              {' '}
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
    
              <div className="flex flex-col-reverse desktop:flex-row gap-3 desktop:mt-8!">
                {isEditingCannedResponse && (
                  <button
                    type="button"
                    onClick={handleAddResponse}
                    className="button -secondary max-w-none!"
                    data-analytics-type="button"
                    data-analytics-click="Add another response"
                  >
                    + Add another response
                  </button>
                )}
                {/* Enable Clear button only if subject or message has changed from initial values */}
                {(() => {
                  const form = responseForms[0];
                  const isClearEnabled =
                    form.subject !== initialSubjectRef.current ||
                    form.message !== initialMessageRef.current;
                  return (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="button -light max-w-none!"
                      data-analytics-type="button"
                      data-analytics-click="Clear"
                      disabled={!isClearEnabled}
                    >
                      Clear
                    </button>
                  );
                })()}
                <button
                  type="submit"
                  className="button -primary -light max-w-none!"
                  data-analytics-type="button"
                  data-analytics-click="Save responses"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Right column - Available Canned Responses List */}
        <div className={showForms ? "w-[400px] flex-shrink-0" : "mb-8"} style={showForms ? {} : {background: 'var(--color-blue-5, #F2F8FB)', padding: '24px', borderRadius: '10px', width: '500px'}}>
          <div id='status' style={showForms ? {background: 'var(--color-blue-5, #F2F8FB)', padding: '24px', borderRadius: '10px'} : {}}>
          <div className='p2 -medium mb-4' style={{color: '#003B71'}}>Available canned responses</div>
            {cannedResponseOptions.map((option) => (
              <div key={option.crId} className='p4 -medium' style={{color: '#0074AD', paddingTop: '8px'}}>
                <button 
                  onClick={() => handleCannedResponseClick(String(option.crId))}
                  className="cursor-pointer hover:underline bg-transparent border-0 p-0 text-inherit font-inherit"
                  type="button"
                >
                  {option.crName}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      </div>
  
      <ScrollToTopBtn />
        </>
      )}
      <ScrollToTopBtn />
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        title="Delete"
        clearBtn="Cancel"
        submitBtn="Delete"
        onSecondary={handleCancelDelete}
        onPrimary={handleConfirmDelete}
        onClose={handleCancelDelete}
      >
        <div className="flex items-center gap-3">
          <img src="/assets/images/alertSolid.svg" alt="alert-icon" className="w-10 h-10 flex-shrink-0" />
          <p className="p3 -book">Are you sure you want to delete this canned response?</p>
        </div>
        <style>{`
          #modal-manual-trig .modal-container {
            max-width: 45% !important;
            width: 90% !important;
            top: 20% !important;
          }
        `}</style>
      </Modal>
     </div>
    );
};

export default ManageCannedResponses;

